import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/lib/company";
import { preloadRoute } from "@/lib/route-preload";
import { canonicalUrl, normalizeOrigin, pageMeta } from "@/lib/seo";
import { useHydrated } from "@/lib/use-hydrated";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { Menu, X } from "lucide-react";
import { type MouseEvent, type PointerEvent, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { to: "/guide", label: "사용 가이드" },
  { to: "/faq", label: "자주 묻는 질문" },
];

const SITE_ORIGIN = normalizeOrigin(import.meta.env.VITE_SITE_ORIGIN);

/** 페이지 전환에서 지금 화면이 물러나는 시간(ms) — globals.css 의 route-leave 와 같다. */
const LEAVE_MS = 320;

/** 앱 안 이동으로 처리할 링크면 그 경로, 아니면 null(새 탭·외부·같은 페이지 안 이동 등). */
function internalTarget(event: MouseEvent | PointerEvent): string | null {
  const anchor = (event.target as Element | null)?.closest?.("a[href]");
  if (!(anchor instanceof HTMLAnchorElement)) return null;
  if (anchor.target && anchor.target !== "_self") return null;
  if (anchor.hasAttribute("download") || anchor.origin !== window.location.origin) return null;
  if (anchor.pathname === window.location.pathname) return null; // #앵커 · 같은 페이지
  return anchor.pathname + anchor.search + anchor.hash;
}

/**
 * Shell for the public marketing/docs pages: sticky translucent header with
 * the brand lockup + section nav + login CTA, and a shared footer.
 */
export function PublicLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  // Signed-in operators get a direct console link instead of the login CTA.
  // 프리렌더는 늘 로그아웃 상태로 그려지므로 hydrate 가 끝난 뒤에 로그인 여부를 반영한다.
  const hydrated = useHydrated();
  const isAuthed = useAuthStore((s) => Boolean(s.token)) && hydrated;
  const consoleTo = isAuthed ? "/admin" : "/admin/login";
  // 페이지를 옮길 때만 본문이 살짝 떠오르며 들어온다. 첫 진입에는 걸지 않는다(LCP 를 늦춘다).
  const firstRender = useRef(true);
  useEffect(() => {
    firstRender.current = false;
  }, []);

  // 페이지 전환: 지금 화면이 살짝 떠오르며 물러나는 동안 헤더 아래로 선이 그어지고, 다음
  // 화면이 올라올 때 선이 오른쪽으로 빠져나간다(랜딩의 "선을 따라 그려진다"). 링크에 닿으면
  // 다음 화면 청크를 미리 받아 둬서 전환이 네트워크를 기다리지 않는다. 동작 줄이기에선 끈다.
  const navigate = useNavigate();
  const [phase, setPhase] = useState<"idle" | "leave" | "enter">("idle");
  const leaveTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(leaveTimer.current), []);
  const warm = (event: PointerEvent) => {
    const to = internalTarget(event);
    if (to) preloadRoute(new URL(to, window.location.origin).pathname);
  };
  const onClickCapture = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const to = internalTarget(event);
    if (!to) return;
    event.preventDefault(); // Link 는 defaultPrevented 면 스스로 이동하지 않는다
    setMenuOpen(false);
    preloadRoute(new URL(to, window.location.origin).pathname);
    setPhase("leave");
    window.clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(() => {
      navigate(to);
      setPhase("enter");
      leaveTimer.current = window.setTimeout(() => setPhase("idle"), 700);
    }, LEAVE_MS);
  };

  // canonical/og:url 은 이 레이아웃(=색인 허용 라우트)에서만 심는다. index.html 에 박으면
  // /g/<token>·/p/<token> 에도 같이 붙어 토큰 URL 이 색인 대상으로 선언되고, og:url 때문에
  // 공유 카드가 랜딩으로 연결돼 게스트가 자기 안내 페이지에 못 간다.
  // 프리렌더된 페이지는 이 두 태그를 이미 <head> 에 갖고 온다 — 있으면 그걸 쓰고(중복 방지),
  // 떠날 때는 어느 쪽이든 걷어낸다.
  useEffect(() => {
    const href = canonicalUrl(SITE_ORIGIN, location.pathname);
    if (!href) return;
    const link =
      document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]') ??
      Object.assign(document.createElement("link"), { rel: "canonical" });
    link.href = href;
    const ogUrl =
      document.head.querySelector<HTMLMetaElement>('meta[property="og:url"]') ??
      document.createElement("meta");
    ogUrl.setAttribute("property", "og:url");
    ogUrl.content = href;
    document.head.append(link, ogUrl);
    return () => {
      link.remove();
      ogUrl.remove();
    };
  }, [location.pathname]);

  // 제목·설명도 여기서 갈아 끼운다. index.html 의 값은 모든 라우트에 고정이라
  // /guide·/faq·/terms·/privacy 가 검색 결과에서 전부 같은 제목으로 뜬다.
  // canonical 과 같은 조건(색인 허용 경로)에서만 바꾸므로 토큰 URL 은 손대지 않는다.
  useEffect(() => {
    const meta = pageMeta(location.pathname);
    if (!meta) return;
    // 되돌릴 값은 셸(index.html)의 기본값이다. 프리렌더된 페이지는 <head> 에 이미 그 페이지의
    // 값이 들어 있어서, "직전 값"으로 되돌리면 /admin 같은 곳에 랜딩 제목이 남는다 — 그래서
    // 프리렌더가 셸 값을 data-shell 에 적어 둔다(scripts/prerender.ts).
    const shell = (el: Element | null) =>
      el instanceof HTMLElement ? el.dataset.shell : undefined;
    const titleEl = document.querySelector("title");
    const previousTitle = shell(titleEl) ?? document.title;
    document.title = meta.title;
    // description·og:title·og:description 은 index.html 이 이미 갖고 있으니
    // 새로 만들지 않고 값만 바꿨다가 되돌린다 — 만들어 붙이면 언마운트 뒤에
    // 원래 태그가 사라진 채 남는다.
    const targets = [
      document.querySelector('meta[name="description"]'),
      document.querySelector('meta[property="og:description"]'),
    ].filter((el): el is HTMLMetaElement => el instanceof HTMLMetaElement);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const previous = targets.map((el) => shell(el) ?? el.content);
    const previousOgTitle =
      ogTitle instanceof HTMLMetaElement ? (shell(ogTitle) ?? ogTitle.content) : null;
    for (const el of targets) el.content = meta.description;
    if (ogTitle instanceof HTMLMetaElement) ogTitle.content = meta.title;
    return () => {
      document.title = previousTitle;
      targets.forEach((el, i) => {
        el.content = previous[i];
      });
      if (ogTitle instanceof HTMLMetaElement && previousOgTitle !== null) {
        ogTitle.content = previousOgTitle;
      }
    };
  }, [location.pathname]);

  return (
    <div
      className="bg-atmosphere grain flex min-h-dvh flex-col"
      onClickCapture={onClickCapture}
      onPointerOverCapture={warm}
    >
      <div aria-hidden="true" className="route-line" data-phase={phase} />
      <a href="#main" className="skip-link">
        본문으로 건너뛰기
      </a>
      <header className="public-header sticky top-0 z-40 border-b border-border/60 bg-background/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link
            to="/"
            aria-label="Stayline 홈"
            onClick={() => setMenuOpen(false)}
            className="brand-link rounded-[10px]"
          >
            <Logo size={34} withWordmark />
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="주요 메뉴">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "nav-draw mx-3 py-2 text-sm font-medium",
                    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Button asChild size="sm" className="btn-sweep ml-3">
              <Link to={consoleTo}>{isAuthed ? "콘솔로 이동" : "로그인"}</Link>
            </Button>
          </nav>

          <button
            type="button"
            className="inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-foreground hover:bg-secondary/60 md:hidden"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "메뉴 닫기" : "메뉴 열기"}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {menuOpen && (
          <nav
            className="animate-rise border-t border-border/60 bg-background/95 px-4 pb-4 pt-2 md:hidden"
            aria-label="모바일 메뉴"
          >
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "block rounded-lg px-3 py-2.5 text-sm font-medium",
                    isActive ? "bg-secondary text-foreground" : "text-muted-foreground"
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Button asChild className="mt-2 w-full">
              <Link to={consoleTo} onClick={() => setMenuOpen(false)}>
                {isAuthed ? "콘솔로 이동" : "로그인"}
              </Link>
            </Button>
          </nav>
        )}
      </header>

      <main
        id="main"
        tabIndex={-1}
        key={location.pathname}
        className={cn(
          "relative z-10 flex-1 outline-none",
          !firstRender.current && "route-enter",
          phase === "leave" && "route-leave"
        )}
      >
        <Outlet />
      </main>

      <footer className="relative z-10 border-t border-border/60 bg-background/70">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-10 sm:px-6 md:flex-row md:items-center md:justify-between">
          <div className="space-y-2">
            <Logo size={30} withWordmark />
            <p className="text-xs text-muted-foreground">
              숙소를 위한 AI 게스트 컨시어지 · PMS · 채널 연동
            </p>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm" aria-label="푸터 메뉴">
            <Link
              to="/guide"
              className="inline-block py-1 text-muted-foreground hover:text-foreground"
            >
              사용 가이드
            </Link>
            <Link
              to="/faq"
              className="inline-block py-1 text-muted-foreground hover:text-foreground"
            >
              자주 묻는 질문
            </Link>
            <Link
              to={consoleTo}
              className="inline-block py-1 text-muted-foreground hover:text-foreground"
            >
              {isAuthed ? "콘솔로 이동" : "운영자 로그인"}
            </Link>
          </nav>
        </div>
        <div className="border-t border-border/40">
          <div className="mx-auto w-full max-w-6xl space-y-3 px-4 py-6 sm:px-6">
            <nav className="flex flex-wrap gap-x-4 gap-y-1 text-xs" aria-label="약관 및 정책">
              <Link
                to="/terms"
                className="inline-block py-1 text-muted-foreground hover:text-foreground"
              >
                이용약관
              </Link>
              {/* 개인정보처리방침은 관례에 따라 강조 표기 */}
              <Link
                to="/privacy"
                className="inline-block py-1 font-semibold text-foreground/80 hover:text-foreground"
              >
                개인정보 처리방침
              </Link>
            </nav>
            <p className="text-xs leading-relaxed text-muted-foreground">
              {COMPANY.name} · 대표 {COMPANY.ceo} · 사업자등록번호 {COMPANY.bizRegNo} ·
              통신판매업신고번호 {COMPANY.ecommerceRegNo}
              <br />
              {COMPANY.address} · 이메일 {COMPANY.email} · 전화 {COMPANY.phone}
            </p>
            {/* 프리렌더는 빌드한 해로 찍힌다. 해가 바뀐 뒤 hydrate 해도 어긋남 경고 없이 넘어간다. */}
            <p className="text-xs text-muted-foreground" suppressHydrationWarning>
              © {new Date().getFullYear()} {COMPANY.name} All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
